let skipNotifications = false;

export function withoutNotifications<T, Args extends readonly unknown[]>(
  cb: (...args: Args) => T,
  ...args: Args
): T {
  const prevSkipNotifications = skipNotifications;
  skipNotifications = true;
  try {
    return cb(...args);
  } finally {
    skipNotifications = prevSkipNotifications;
  }
}

const pendingNotifiers = new Set<() => void>();

function flushNotifiers() {
  try {
    for (const notify of pendingNotifiers) {
      if (!skipNotifications) {
        notify();
      }
    }
  } finally {
    pendingNotifiers.clear();
  }
}

export type Listener = () => void;
export type Unsubscribe = () => void;

export interface Notifier {
  notify: () => void;
  listen: (listener: Listener) => Unsubscribe;
  unlisten: (listener: Listener) => void;
  clear: () => void;
  isEmpty(): boolean;
}

export function createNotifier(): Notifier {
  const listeners = new Set<Listener>();

  function listen(listener: Listener): Unsubscribe {
    listeners.add(listener);
    return unlisten.bind(undefined, listener);
  }

  function unlisten(listener: Listener): void {
    listeners.delete(listener);
  }

  function notify() {
    for (const listener of listeners) {
      try {
        listener();
      } catch (error) {
        // This is a global function to report an error without failing it
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/reportError
        reportError(error);
      }
    }
  }

  function scheduleNotify() {
    if (pendingNotifiers.size === 0) {
      queueMicrotask(flushNotifiers);
    }
    pendingNotifiers.add(notify);
  }

  return {
    notify: scheduleNotify,
    listen,
    unlisten,
    clear() {
      listeners.clear();
    },
    isEmpty() {
      return listeners.size === 0;
    },
  };
}
